/**
 * lk-motion.js — scroll-triggered motion for the LK rebrand
 * (client feedback, Blake Loom 2026-08-31: fades, scroll-into-focus, parallax).
 *
 * - [data-lk-motion="fade|fade-up|focus"] gains .is-inview on intersection;
 *   the states/transitions live in snippets/lk-motion-styles.liquid.
 * - [data-lk-parallax="<max px>"] drifts vertically with scroll via the
 *   standalone `translate` property (composes with CSS transform rotations).
 * - prefers-reduced-motion: everything lands in its resting state, no parallax.
 * - Re-scans on shopify:section:load so the theme editor stays live.
 */
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

let observer = null;
const parallaxEls = new Set();

function reveal(el) {
  el.classList.add('is-inview');
}

function scan(root) {
  const scope = root && root.querySelectorAll ? root : document;
  scope.querySelectorAll('[data-lk-motion]:not(.is-inview)').forEach((el) => {
    if (REDUCED.matches || !observer) reveal(el);
    else observer.observe(el);
  });
  if (!REDUCED.matches) {
    scope.querySelectorAll('[data-lk-parallax]').forEach((el) => parallaxEls.add(el));
  }
}

let ticking = false;
function onScroll() {
  if (ticking || REDUCED.matches) return;
  ticking = true;
  requestAnimationFrame(() => {
    const vh = window.innerHeight;
    parallaxEls.forEach((el) => {
      if (!el.isConnected) {
        parallaxEls.delete(el);
        return;
      }
      const max = parseFloat(el.dataset.lkParallax) || 14;
      const rect = el.getBoundingClientRect();
      // -1 (element center at viewport bottom) … 1 (at viewport top)
      const progress = 1 - (rect.top + rect.height / 2) / (vh / 2);
      const clamped = Math.max(-1, Math.min(1, progress));
      el.style.translate = `0 ${(clamped * max).toFixed(1)}px`;
    });
    ticking = false;
  });
}

function init() {
  if (!REDUCED.matches && 'IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            reveal(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );
  }
  scan(document);
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
  document.addEventListener('shopify:section:load', (e) => scan(e.target));
  REDUCED.addEventListener('change', () => {
    if (REDUCED.matches) {
      document.querySelectorAll('[data-lk-motion]').forEach(reveal);
      parallaxEls.forEach((el) => (el.style.translate = ''));
      parallaxEls.clear();
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
